import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk, Buildings, Upload } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { jobsApi, careerApi, EMPLOYMENT_TYPES, WORK_MODES, EXPERIENCE_LEVELS, INDUSTRY_OPTIONS } from "@/lib/jobsApi";
import { toast } from "sonner";

/** Post / edit a job listing. If :jobId is present in the URL, we're editing. */
export default function PostJob() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(jobId);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    title: "",
    company_name: "",
    company_logo: "",
    description: "",
    responsibilities: "",
    qualifications: "",
    skills: "",
    experience_level: "entry",
    experience_years: "",
    employment_type: "full-time",
    work_mode: "onsite",
    industry: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "USD",
    salary_negotiable: false,
    location: "",
    vacancies: 1,
    application_deadline: "",
    contact_email: "",
    application_link: "",
  });

  useEffect(() => {
    if (!editing) return;
    jobsApi.detail(jobId).then((j) => {
      setF({
        title: j.title || "",
        company_name: j.employer_company_name || "",
        company_logo: j.employer_company_logo || "",
        description: j.description || "",
        responsibilities: j.responsibilities || "",
        qualifications: j.qualifications || "",
        skills: (j.skills || []).join(", "),
        experience_level: j.experience_level || "entry",
        experience_years: j.experience_years || "",
        employment_type: j.employment_type || "full-time",
        work_mode: j.work_mode || "onsite",
        industry: j.industry || "",
        salary_min: j.salary?.min ?? "",
        salary_max: j.salary?.max ?? "",
        salary_currency: j.salary?.currency || "USD",
        salary_negotiable: !!j.salary?.negotiable,
        location: j.location || "",
        vacancies: j.vacancies || 1,
        application_deadline: j.application_deadline?.slice(0, 10) || "",
        contact_email: j.contact_email || "",
        application_link: j.application_link || "",
      });
    }).catch(() => { toast.error("Job not found"); navigate("/jobs"); });
  }, [jobId, editing, navigate]);

  const set = (k, v) => setF({ ...f, [k]: v });

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await careerApi.upload(file, "logo");
      set("company_logo", res.url);
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.title.trim() || !f.company_name.trim() || !f.description.trim()) {
      toast.error("Title, company, and description are required");
      return;
    }
    setSaving(true);
    const body = {
      title: f.title, company_name: f.company_name, company_logo: f.company_logo || null,
      description: f.description, responsibilities: f.responsibilities,
      qualifications: f.qualifications,
      skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
      experience_level: f.experience_level, experience_years: f.experience_years,
      employment_type: f.employment_type, work_mode: f.work_mode,
      industry: f.industry || null,
      salary: {
        min: f.salary_min ? Number(f.salary_min) : null,
        max: f.salary_max ? Number(f.salary_max) : null,
        currency: f.salary_currency,
        negotiable: f.salary_negotiable,
      },
      location: f.location,
      vacancies: Number(f.vacancies) || 1,
      application_deadline: f.application_deadline || null,
      contact_email: f.contact_email || null,
      application_link: f.application_link || null,
    };
    try {
      const saved = editing ? await jobsApi.update(jobId, body) : await jobsApi.create(body);
      toast.success(editing ? "Job updated" : "Job posted!");
      navigate(`/jobs/${saved.job_id || jobId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-4" data-testid="post-job-page">
      <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>
      <h1 className="font-heading text-3xl md:text-4xl font-black">{editing ? "Edit job" : "Post a new job"}</h1>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Company</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Company name *">
            <Input data-testid="pj-company" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Company logo (optional)">
            <div className="flex items-center gap-3">
              {f.company_logo ? (
                <img src={f.company_logo} alt="" className="w-14 h-14 rounded-lg object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center">
                  <Buildings size={22} className="text-white/40" weight="duotone" />
                </div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/15 bg-transparent hover:bg-white/5 text-sm">
                <Upload size={14} weight="bold" />
                {uploading ? "Uploading…" : "Upload logo"}
                <input data-testid="pj-logo-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
              {f.company_logo && <Button variant="ghost" size="sm" onClick={() => set("company_logo", "")}>Remove</Button>}
            </div>
          </Field>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Position</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Job title *">
            <Input data-testid="pj-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Senior React Engineer" className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Description *">
            <Textarea data-testid="pj-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={5} className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Responsibilities">
            <Textarea data-testid="pj-responsibilities" value={f.responsibilities} onChange={(e) => set("responsibilities", e.target.value)} rows={4} className="bg-white/5 border-white/10" placeholder="One per line" />
          </Field>
          <Field label="Qualifications">
            <Textarea data-testid="pj-qualifications" value={f.qualifications} onChange={(e) => set("qualifications", e.target.value)} rows={4} className="bg-white/5 border-white/10" placeholder="Required education, certifications, etc." />
          </Field>
          <Field label="Skills (comma-separated)">
            <Input data-testid="pj-skills" value={f.skills} onChange={(e) => set("skills", e.target.value)} placeholder="python, docker, aws" className="bg-white/5 border-white/10" />
          </Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Type">
              <PickerRaw testId="pj-type" value={f.employment_type} onChange={(v) => set("employment_type", v)} options={EMPLOYMENT_TYPES} />
            </Field>
            <Field label="Work mode">
              <PickerRaw testId="pj-mode" value={f.work_mode} onChange={(v) => set("work_mode", v)} options={WORK_MODES} />
            </Field>
            <Field label="Experience">
              <PickerRaw testId="pj-experience" value={f.experience_level} onChange={(v) => set("experience_level", v)} options={EXPERIENCE_LEVELS} />
            </Field>
            <Field label="Industry">
              <PickerRaw testId="pj-industry" value={f.industry} onChange={(v) => set("industry", v)} options={[{ value: "", label: "—" }, ...INDUSTRY_OPTIONS.map((i) => ({ value: i, label: i }))]} />
            </Field>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Years of experience">
              <Input data-testid="pj-years" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} placeholder="e.g. 3-5" className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Location">
              <Input data-testid="pj-location" value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="City, Country" className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Vacancies">
              <Input data-testid="pj-vacancies" type="number" min={1} value={f.vacancies} onChange={(e) => set("vacancies", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Compensation</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Min salary">
              <Input data-testid="pj-salary-min" type="number" value={f.salary_min} onChange={(e) => set("salary_min", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Max salary">
              <Input data-testid="pj-salary-max" type="number" value={f.salary_max} onChange={(e) => set("salary_max", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Currency">
              <Input data-testid="pj-currency" value={f.salary_currency} onChange={(e) => set("salary_currency", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input data-testid="pj-negotiable" type="checkbox" checked={f.salary_negotiable} onChange={(e) => set("salary_negotiable", e.target.checked)} />
            Salary is negotiable
          </label>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Application</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Deadline">
              <Input data-testid="pj-deadline" type="date" value={f.application_deadline} onChange={(e) => set("application_deadline", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Contact email">
              <Input data-testid="pj-contact" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <Field label="External application link (optional)">
            <Input data-testid="pj-external" value={f.application_link} onChange={(e) => set("application_link", e.target.value)} placeholder="https://…" className="bg-white/5 border-white/10" />
          </Field>
        </CardContent>
      </Card>

      <Button data-testid="pj-submit" onClick={submit} disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-black text-base gap-2">
        <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : editing ? "Save changes" : "Publish job"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PickerRaw({ value, onChange, options, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="bg-white/5 border-white/10">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o.value || "_"} value={o.value || "_"} disabled={!o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
